import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { CasLoader, CasLoaderProps } from "./CasLoader";
import { LoadStatus } from "../../hook/loadStatus";

// The one wrapper every load path uses. It renders Cas's loader (CasLoader, the app's only spinner) while a value is
// in flight, an error with Retry when the fetch failed, and the children only when the value has arrived.
export interface LoadBoundaryProps {
  status: LoadStatus;
  /** Called by the Retry button. With no handler the error shows no button. */
  onRetry?: () => void;
  /** Accessible name of the loader. */
  label?: string;
  errorMessage?: string;
  size?: number;
  variant?: CasLoaderProps["variant"];
  /** Grow to fill a pane (a whole page) instead of sitting inline (one block). */
  fill?: boolean;
  /** Loading wrapper gets this id, the error wrapper `${testId}-error`, the button `${testId}-retry`. */
  testId?: string;
  children?: React.ReactNode;
}

export const LoadBoundary: React.FC<LoadBoundaryProps> = ({
  status,
  onRetry,
  label = "Loading",
  errorMessage = "Could not load this.",
  size = 32,
  variant,
  fill = false,
  testId = "load-boundary",
  children,
}) => {
  if (status === "ready") return <>{children}</>;
  const frame = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    py: 2,
    ...(fill ? { flexGrow: 1, minHeight: 200 } : {}),
  } as const;
  if (status === "error") {
    return (
      <Box sx={frame} role="alert" data-testid={`${testId}-error`} data-status="error">
        <Typography variant="body2" color="text.secondary">
          {errorMessage}
        </Typography>
        {onRetry && (
          <Button size="small" variant="outlined" onClick={onRetry} data-testid={`${testId}-retry`}>
            Retry
          </Button>
        )}
      </Box>
    );
  }
  return (
    <Box sx={frame} data-testid={testId} data-status="loading">
      <CasLoader size={size} label={label} variant={variant} />
    </Box>
  );
};
